import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Telescope } from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';

/* "We see…" vignettes — the aspirational core of the vision page. */

const WE_SEE = [
  {
    title: 'Young adults',
    body:
      'Entering life with emotional control, communication skills, financial intelligence and leadership ability — not learning these only after failure forces them to.',
  },
  {
    title: 'Professionals',
    body:
      'Sharpening their mindset and systems instead of burning out. Treating growth like training, not therapy.',
  },
  {
    title: 'Communities',
    body:
      'Built around accountability, not comparison. People holding the same standard, lifting each other.',
  },
  {
    title: 'A generation',
    body:
      'That treats personal growth as a long-term practice, not a temporary phase. Discipline as default, not heroism.',
  },
];

const Vision = () => {
  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* HERO — slightly more sky-saturated than other pages, since vision
          lives in the sky / horizon metaphor. */}
      <section className="relative pt-[110px] md:pt-[170px] pb-14 md:pb-28 px-6 overflow-hidden min-h-[580px] md:min-h-[760px]">
        <AltParallaxBackdrop />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7"
            data-testid="vision-eyebrow"
          >
            <Telescope className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2.5} />
            Our vision
          </span>
          <h1
            className="text-5xl md:text-7xl lg:text-[88px] font-black leading-[0.98] tracking-tight text-[#0b1f3a] alt-hero-glow"
          >
            Change the way
            <br />
            the world{' '}
            <em className="italic font-light text-blue-600">grows.</em>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            A world where personal development is structured, shared and lifelong — where
            discipline replaces inspiration, skill replaces information, and a global
            Community makes long-term growth inevitable.
          </p>
        </div>
      </section>

      {/* CRITIQUE — flowing prose instead of a 3-card grid. */}
      <section className="px-6 py-24 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-500 mb-5 text-center">
            The world that is
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.08] tracking-tight text-[#0b1f3a] text-center mb-12">
            Personal development today is{' '}
            <em className="italic font-light text-rose-500">crowded, loud, fragmented.</em>
          </h2>
          <div className="space-y-7 text-lg md:text-xl text-[#0b1f3a]/80 leading-relaxed">
            <p>
              Open any app, any feed, any podcast and the message is the same:{' '}
              <em>you can be more</em>. The promise is transformation. What
              actually arrives is information — another quote, another
              framework, another five-step list to add to the stack you never
              implemented from last year.
            </p>
            <p>
              The industry sells inspiration because inspiration is easy to
              package and easy to scroll. Discipline is harder to film. So we
              get the highlight reel — the moment of revelation, the morning
              routine in soft light — without the boring middle, where the work
              actually happens. Excitement is everywhere. Systems are nowhere.
            </p>
            <p className="text-2xl md:text-3xl font-bold text-[#0b1f3a] pt-4">
              Growth deserves{' '}
              <em className="italic font-light text-blue-600">better.</em>
            </p>
          </div>
        </div>
      </section>

      {/* THE SHIFT — manifesto prose instead of 6 from→to comparison rows. */}
      <section className="px-6 py-24 md:py-28 bg-[#0b1f3a] text-white">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-5 text-center">
            The shift
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.08] tracking-tight text-center mb-12">
            From the world that{' '}
            <em className="italic font-light text-blue-300">is</em>{' '}
            to the one we&rsquo;re{' '}
            <em className="italic font-light text-blue-300">building.</em>
          </h2>
          <div className="space-y-7 text-lg md:text-xl text-white/80 leading-relaxed">
            <p>
              We are building toward a world where information becomes
              capability. Where the books you&rsquo;ve read, the videos you&rsquo;ve
              watched, the courses you&rsquo;ve started but never finished — all of
              that finally has a place to land. A practice. A muscle. Something
              you can actually do, not just something you know about.
            </p>
            <p>
              A world where inspiration is replaced with discipline, and that
              isn&rsquo;t a downgrade — it&rsquo;s freedom. Discipline doesn&rsquo;t require
              waking up feeling motivated. It just requires the system. The
              system shows up, and you show up with it. Hype gets replaced with
              skill. Comparison gets replaced with accountability. Random
              content gets replaced with a clear path.
            </p>
            <p>
              That is the shift. Quieter than the marketing of personal
              development today, and a hundred times more durable.
            </p>
          </div>
        </div>
      </section>

      {/* WE SEE — vignettes. The aspirational core. */}
      <section className="px-6 py-20 md:py-28 bg-gradient-to-b from-white to-[#eaf3fb]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            We imagine a world where
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-14">
            People don&rsquo;t consume content.{' '}
            <em className="italic font-light text-blue-600">They train skills.</em>
          </h2>

          <div className="grid md:grid-cols-2 gap-5">
            {WE_SEE.map((card, i) => (
              <article
                key={card.title}
                className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-7 md:p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[11px] font-mono text-blue-600 font-semibold">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="h-px flex-1 bg-blue-200" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600">
                    We see
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-[#0b1f3a] leading-tight mb-3">
                  {card.title}
                </h3>
                <p className="text-[#0b1f3a]/75 leading-relaxed">{card.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* THE STACK — three lines, dark navy, manifesto-style. */}
      <section className="px-6 py-20 md:py-28 bg-[#0b1f3a] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-7">
            In the world we&rsquo;re building
          </p>
          <div className="space-y-4 text-3xl md:text-5xl font-black leading-tight tracking-tight">
            <p>
              Discipline becoming{' '}
              <em className="italic font-light text-blue-300">normal.</em>
            </p>
            <p>
              Structure becoming{' '}
              <em className="italic font-light text-blue-300">empowering.</em>
            </p>
            <p>
              Growth becoming{' '}
              <em className="italic font-light text-blue-300">identity.</em>
            </p>
          </div>
        </div>
      </section>

      {/* CLOSING — the future tense. */}
      <section className="px-6 py-20 md:py-28 bg-gradient-to-b from-[#eaf3fb] to-white">
        <div className="max-w-3xl mx-auto text-center space-y-4 text-xl md:text-2xl text-[#0b1f3a]/80 leading-relaxed">
          <p>We are not building another course.</p>
          <p className="font-semibold text-[#0b1f3a]">
            We are building a long-term environment for transformation.
          </p>
          <p className="pt-4">A system that outlasts trends.</p>
          <p>A framework that outgrows motivation.</p>
          <p>
            A global structure where growth is no longer random — but{' '}
            <span className="font-semibold text-[#0b1f3a]">intentional, guided, sustained.</span>
          </p>
          <p className="pt-8 text-3xl md:text-5xl font-black tracking-tight text-[#0b1f3a]">
            That is the future we are{' '}
            <em className="italic font-light text-blue-600">building.</em>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 md:py-28 bg-[#0b1f3a]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-black leading-tight">
            Want to be part of{' '}
            <em className="italic font-light text-blue-300">this future?</em>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-white/75">
            The first step is downloading the app. The second is showing up tomorrow.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/download-app"
              data-testid="vision-cta-download"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#0b1f3a] font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Download Levanta
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/mission"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white/5 text-white font-semibold ring-1 ring-white/20 hover:bg-white/10 transition-all duration-200"
            >
              Read our mission
            </Link>
          </div>
        </div>
      </section>

      <AltFooter />
    </div>
  );
};

export default Vision;
